import React, { useState } from 'react';
import { Button, Card, Cards, CardHeader, CardBody, CardFooter, Typography, Stack, Table, Input, Dropdown, Checkbox, Popup } from '@prometheus/components';

export const ComponentsDemo: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [numberValue, setNumberValue] = useState('');
  const [dropdownValue, setDropdownValue] = useState<string | number>('');
  const [customDropdownValue, setCustomDropdownValue] = useState<string | number>('');
  
  // Checkbox states
  const [basicCheckbox, setBasicCheckbox] = useState(false);
  const [checkedCheckbox, setCheckedCheckbox] = useState(true);
  const [indeterminateCheckbox, setIndeterminateCheckbox] = useState(false);
  const [errorCheckbox, setErrorCheckbox] = useState(false);
  const [successCheckbox, setSuccessCheckbox] = useState(true);
  const [disabledCheckbox, setDisabledCheckbox] = useState(false);
  const [gameSettings, setGameSettings] = useState({
    soundEnabled: true,
    musicEnabled: false,
    notifications: true,
    autoSave: true,
    hardcoreMode: false,
    pvpEnabled: false,
  });
  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h2>Prometheus Components Demo</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Buttons</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="tertiary">Tertiary</Button>
          <Button variant="success">Success</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="warning">Warning</Button>
          <Button variant="info">Info</Button>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <Button outline variant="primary">Primary</Button>
          <Button outline variant="secondary">Secondary</Button>
          <Button outline variant="tertiary">Tertiary</Button>
          <Button outline variant="success">Success</Button>
          <Button outline variant="danger">Danger</Button>
          <Button outline variant="warning">Warning</Button>
          <Button outline variant="info">Info</Button>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <Button size="sm">Small</Button>
          <Button size="base">Base</Button>
          <Button size="lg">Large</Button>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button loading>Loading...</Button>
          <Button disabled>Disabled</Button>
          <Button fullWidth>Full Width</Button>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Cards</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Basic Card Variants</h4>
          <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <Card>
              <CardHeader>Default Card</CardHeader>
              <CardBody>
                This is a default card with a neutral border and header, body, and footer sections.
              </CardBody>
              <CardFooter>
                <Button size="sm" variant="primary">Action</Button>
              </CardFooter>
            </Card>

            <Card variant="elevated">
              <CardHeader>Elevated Card</CardHeader>
              <CardBody>
                This card has elevation with shadow effects.
              </CardBody>
            </Card>

            <Card variant="outlined">
              <CardHeader>Outlined Card</CardHeader>
              <CardBody>
                This card has a prominent border outline.
              </CardBody>
            </Card>

            <Card interactive onClick={() => alert('Card clicked!')}>
              <CardBody>
                <strong>Interactive Card</strong><br />
                Click me! I have hover effects and am clickable.
              </CardBody>
            </Card>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Border Color Variants</h4>
          <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Card borderColor="primary">
              <CardBody>
                <strong>Primary Border</strong><br />
                Bright lime green border for primary actions and highlights.
              </CardBody>
            </Card>

            <Card borderColor="secondary">
              <CardBody>
                <strong>Secondary Border</strong><br />
                Cyan blue border for secondary elements and information.
              </CardBody>
            </Card>

            <Card borderColor="tertiary">
              <CardBody>
                <strong>Tertiary Border</strong><br />
                Purple border for tertiary actions and special content.
              </CardBody>
            </Card>

            <Card borderColor="success">
              <CardBody>
                <strong>Success Border</strong><br />
                Green border for successful operations and positive feedback.
              </CardBody>
            </Card>

            <Card borderColor="danger">
              <CardBody>
                <strong>Danger Border</strong><br />
                Red border for warnings, errors, and critical actions.
              </CardBody>
            </Card>

            <Card borderColor="warning">
              <CardBody>
                <strong>Warning Border</strong><br />
                Yellow border for caution, alerts, and important notices.
              </CardBody>
            </Card>

            <Card borderColor="info">
              <CardBody>
                <strong>Info Border</strong><br />
                Blue border for informational content and help text.
              </CardBody>
            </Card>

            <Card borderColor="light">
              <CardBody>
                <strong>Light Border</strong><br />
                Light colored border for subtle emphasis and backgrounds.
              </CardBody>
            </Card>

            <Card borderColor="dark">
              <CardBody>
                <strong>Dark Border</strong><br />
                Dark border for contrast and sophisticated styling.
              </CardBody>
            </Card>

            <Card borderColor="none">
              <CardBody>
                <strong>No Border</strong><br />
                This card has no border at all for a clean, minimal look.
              </CardBody>
            </Card>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Interactive Border Colors</h4>
          <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Card borderColor="primary" interactive onClick={() => alert('Primary card clicked!')}>
              <CardBody>
                <strong>Interactive Primary</strong><br />
                Hover to see the border color change!
              </CardBody>
            </Card>

            <Card borderColor="success" interactive onClick={() => alert('Success card clicked!')}>
              <CardBody>
                <strong>Interactive Success</strong><br />
                Click me for a success action!
              </CardBody>
            </Card>

            <Card borderColor="danger" interactive onClick={() => alert('Danger card clicked!')}>
              <CardBody>
                <strong>Interactive Danger</strong><br />
                Careful - this is a dangerous action!
              </CardBody>
            </Card>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Card Examples</h4>
          <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <Card borderColor="success" variant="elevated">
              <CardHeader>
                <Typography variant="h6" color="success" retro>MISSION COMPLETE</Typography>
              </CardHeader>
              <CardBody>
                <Typography variant="body2">
                  Objective: Secure Alpha Station<br />
                  Reward: 2,500 Credits<br />
                  XP Gained: 150
                </Typography>
              </CardBody>
              <CardFooter>
                <Button size="sm" variant="success">Claim Reward</Button>
              </CardFooter>
            </Card>

            <Card borderColor="danger" interactive>
              <CardHeader>
                <Typography variant="h6" color="danger" retro>CRITICAL ALERT</Typography>
              </CardHeader>
              <CardBody>
                <Typography variant="body2">
                  Hull Integrity: 15%<br />
                  Oxygen Level: Critical<br />
                  Emergency Repairs Needed
                </Typography>
              </CardBody>
              <CardFooter>
                <Button size="sm" variant="danger">Emergency Repair</Button>
              </CardFooter>
            </Card>

            <Card borderColor="info">
              <CardHeader>
                <Typography variant="h6" color="info">Ship Status</Typography>
              </CardHeader>
              <CardBody>
                <Typography variant="body2">
                  Class: Battlecruiser<br />
                  Crew: 1,200/1,500<br />
                  Fuel: 85%<br />
                  Shields: Online
                </Typography>
              </CardBody>
            </Card>

            <Card borderColor="warning" variant="outlined">
              <CardHeader>
                <Typography variant="h6" color="warning" retro>LOW RESOURCES</Typography>
              </CardHeader>
              <CardBody>
                <Typography variant="body2">
                  Food Supplies: 3 days<br />
                  Water: 5 days<br />
                  Medical: 12 days
                </Typography>
              </CardBody>
              <CardFooter>
                <Button size="sm" variant="warning">Resupply</Button>
              </CardFooter>
            </Card>

            <Card borderColor="none" variant="filled">
              <CardBody>
                <Typography variant="h6" retro color="primary" style={{ marginBottom: '0.5rem' }}>
                  STEALTH MODE
                </Typography>
                <Typography variant="body2">
                  This ship is running silent with no visible borders - perfect for covert operations.
                </Typography>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Cards (Stacked)</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Basic Cards Stack</h4>
          <div style={{ maxWidth: '400px' }}>
            <Cards>
              <Card>
                <CardBody>
                  <Typography variant="h6">First Card</Typography>
                  <Typography variant="body2">This is the first card in the stack with rounded top corners.</Typography>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <Typography variant="h6">Middle Card</Typography>
                  <Typography variant="body2">This is a middle card with no rounded corners and no bottom border.</Typography>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <Typography variant="h6">Last Card</Typography>
                  <Typography variant="body2">This is the last card with rounded bottom corners.</Typography>
                </CardBody>
              </Card>
            </Cards>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Menu-Style Navigation</h4>
          <div style={{ maxWidth: '300px' }}>
            <Cards>
              <Card interactive>
                <CardBody>
                  <Typography variant="body1" bold>🏠 Dashboard</Typography>
                  <Typography variant="body2" color="text-muted">View your overview</Typography>
                </CardBody>
              </Card>
              <Card interactive>
                <CardBody>
                  <Typography variant="body1" bold>👥 Users</Typography>
                  <Typography variant="body2" color="text-muted">Manage user accounts</Typography>
                </CardBody>
              </Card>
              <Card interactive>
                <CardBody>
                  <Typography variant="body1" bold>📊 Analytics</Typography>
                  <Typography variant="body2" color="text-muted">View reports and metrics</Typography>
                </CardBody>
              </Card>
              <Card interactive>
                <CardBody>
                  <Typography variant="body1" bold>⚙️ Settings</Typography>
                  <Typography variant="body2" color="text-muted">Configure your preferences</Typography>
                </CardBody>
              </Card>
            </Cards>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Menu</h4>
          <div style={{ maxWidth: '350px' }}>
            <Cards>
              <Card interactive borderColor="primary">
                <CardBody>
                  <Typography variant="h6" color="primary" retro>⚔️ COMBAT</Typography>
                  <Typography variant="body2">Enter the battlefield</Typography>
                </CardBody>
              </Card>
              <Card interactive borderColor="secondary">
                <CardBody>
                  <Typography variant="h6" color="secondary" retro>🛡️ INVENTORY</Typography>
                  <Typography variant="body2">Manage your equipment</Typography>
                </CardBody>
              </Card>
              <Card interactive borderColor="success">
                <CardBody>
                  <Typography variant="h6" color="success" retro>🏆 MISSIONS</Typography>
                  <Typography variant="body2">View available quests</Typography>
                </CardBody>
              </Card>
              <Card interactive borderColor="warning">
                <CardBody>
                  <Typography variant="h6" color="warning" retro>🏪 SHOP</Typography>
                  <Typography variant="body2">Buy and sell items</Typography>
                </CardBody>
              </Card>
              <Card interactive borderColor="info">
                <CardBody>
                  <Typography variant="h6" color="info" retro>📋 STATS</Typography>
                  <Typography variant="body2">Character progression</Typography>
                </CardBody>
              </Card>
            </Cards>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Single Card in Cards Container</h4>
          <div style={{ maxWidth: '300px' }}>
            <Cards>
              <Card interactive borderColor="tertiary">
                <CardBody>
                  <Typography variant="h6" color="tertiary">Single Card</Typography>
                  <Typography variant="body2">
                    This single card maintains all its rounded corners when alone in a Cards container.
                  </Typography>
                </CardBody>
              </Card>
            </Cards>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Typography</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <Stack space="lg">
            <h4>Standard Typography</h4>
            <Typography variant="h1">Heading 1</Typography>
            <Typography variant="h2">Heading 2</Typography>
            <Typography variant="h3">Heading 3</Typography>
            <Typography variant="h4">Heading 4</Typography>
            <Typography variant="h5">Heading 5</Typography>
            <Typography variant="h6">Heading 6</Typography>
            <Typography variant="subtitle1">Subtitle 1</Typography>
            <Typography variant="subtitle2">Subtitle 2</Typography>
            <Typography variant="body1">Body 1 - This is the default body text with comfortable line spacing for reading longer content.</Typography>
            <Typography variant="body2">Body 2 - Smaller body text for secondary content or captions.</Typography>
            <Typography variant="caption">Caption text for small details</Typography>
            <Typography variant="overline">Overline text</Typography>
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Retro/Gaming Typography</h4>
          <Typography variant="h1" retro>Retro Heading 1</Typography>
          <Typography variant="h2" retro>Retro Heading 2</Typography>
          <Typography variant="h3" retro>Retro Heading 3</Typography>
          <Typography variant="body1" retro>This is retro body text using the Chakra Petch display font for that sci-fi HUD feel.</Typography>
          <Typography variant="body2" retro>Smaller retro text with pixelated styling.</Typography>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Color Variants</h4>
          <Typography color="primary">Primary colored text</Typography>
          <Typography color="secondary">Secondary colored text</Typography>
          <Typography color="success">Success colored text</Typography>
          <Typography color="danger">Danger colored text</Typography>
          <Typography color="warning">Warning colored text</Typography>
          <Typography color="info">Info colored text</Typography>
          <Typography color="text-muted">Muted text</Typography>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Text Styling</h4>
          <Typography bold>Bold text</Typography>
          <Typography italic>Italic text</Typography>
          <Typography uppercase>Uppercase text</Typography>
          <Typography capitalize>capitalized text</Typography>
          <Typography align="center">Center aligned text</Typography>
          <Typography align="right">Right aligned text</Typography>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Examples</h4>
          <Typography variant="h2" retro color="primary">GAME OVER</Typography>
          <Typography variant="h3" retro color="success">LEVEL COMPLETE!</Typography>
          <Typography variant="body1" retro color="warning">Press SPACE to continue...</Typography>
          <Typography variant="caption" retro color="text-muted">Score: 12,450</Typography>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Table</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Transaction Log Example</h4>
          <Table
            columns={[
              { key: 'transaction', label: 'Transaction', align: 'left' },
              { key: 'player', label: 'Player', align: 'left' },
              { key: 'amount', label: 'Amount', align: 'right' }
            ]}
            data={[
              {
                transaction: 'Ship Purchase',
                player: 'Commander Alpha',
                amount: '-2,500 Credits'
              },
              {
                transaction: 'Resource Sale',
                player: 'Trader Beta',
                amount: '+1,200 Credits'
              },
              {
                transaction: 'Planet Upgrade',
                player: 'Engineer Gamma',
                amount: '-800 Credits'
              },
              {
                transaction: 'Mission Reward',
                player: 'Captain Delta',
                amount: '+3,000 Credits'
              },
              {
                transaction: 'Fuel Purchase',
                player: 'Pilot Echo',
                amount: '-150 Credits'
              }
            ]}
            cellClassName={(value, column, row) => {
              if (column.key === 'player') {
                return 'prometheus-table-cell--player';
              }
              if (column.key === 'amount') {
                const amount = value as string;
                if (amount.startsWith('+')) {
                  return 'prometheus-table-cell--positive prometheus-table-cell--numeric';
                } else if (amount.startsWith('-')) {
                  return 'prometheus-table-cell--negative prometheus-table-cell--numeric';
                }
                return 'prometheus-table-cell--numeric';
              }
              if (column.key === 'transaction') {
                return 'prometheus-table-cell--transaction';
              }
              return '';
            }}
            hoverable
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Table Variants</h4>
          <div style={{ marginBottom: '15px' }}>
            <h5>Small Size</h5>
            <Table
              size="sm"
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'status', label: 'Status', align: 'center' },
                { key: 'value', label: 'Value', align: 'right' }
              ]}
              data={[
                { name: 'Alpha Station', status: 'Online', value: '100%' },
                { name: 'Beta Outpost', status: 'Offline', value: '0%' },
                { name: 'Gamma Base', status: 'Maintenance', value: '75%' }
              ]}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <h5>Striped Rows</h5>
            <Table
              striped
              columns={[
                { key: 'ship', label: 'Ship' },
                { key: 'class', label: 'Class' },
                { key: 'crew', label: 'Crew', align: 'right' }
              ]}
              data={[
                { ship: 'USS Prometheus', class: 'Battleship', crew: '1,200' },
                { ship: 'HMS Explorer', class: 'Scout', crew: '50' },
                { ship: 'CSS Merchant', class: 'Cargo', crew: '25' },
                { ship: 'IFS Guardian', class: 'Destroyer', crew: '800' }
              ]}
            />
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Input</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Basic Inputs</h4>
          <Stack space="md">
            <Input
              type="text"
              placeholder="Enter your name..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              label="Text Input"
            />
            <Input
              type="number"
              placeholder="Enter a number..."
              value={numberValue}
              onChange={(e) => setNumberValue(e.target.value)}
              label="Number Input"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Input Sizes</h4>
          <Stack space="md">
            <Input
              size="sm"
              placeholder="Small input..."
              label="Small Size"
            />
            <Input
              size="base"
              placeholder="Base input..."
              label="Base Size (Default)"
            />
            <Input
              size="lg"
              placeholder="Large input..."
              label="Large Size"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Input States</h4>
          <Stack space="md">
            <Input
              placeholder="Success state..."
              success
              label="Success Input"
              helperText="This input is in a success state"
            />
            <Input
              placeholder="Error state..."
              error
              label="Error Input"
              helperText="This input has an error"
            />
            <Input
              placeholder="Disabled input..."
              disabled
              label="Disabled Input"
              helperText="This input is disabled"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Full Width Input</h4>
          <Input
            fullWidth
            placeholder="This input takes full width..."
            label="Full Width Input"
            helperText="This input spans the full width of its container"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Examples</h4>
          <Stack space="md">
            <Input
              placeholder="Enter player name..."
              label="Player Name"
              helperText="Choose your commander name"
            />
            <Input
              type="number"
              placeholder="Enter credits amount..."
              label="Credits Transfer"
              helperText="Amount in galactic credits"
            />
            <Input
              placeholder="Enter coordinates..."
              label="Navigation Target"
              helperText="Format: X.XX, Y.YY, Z.ZZ"
            />
          </Stack>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Dropdown</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Basic Dropdown</h4>
          <Stack space="md">
            <Dropdown
              label="Select an Option"
              placeholder="Choose something..."
              items={[
                { value: 'option1', label: 'Option 1' },
                { value: 'option2', label: 'Option 2' },
                { value: 'option3', label: 'Option 3' },
                { value: 'option4', label: 'Option 4 (Disabled)', disabled: true },
                { value: 'option5', label: 'Option 5' },
              ]}
              value={dropdownValue}
              onChange={setDropdownValue}
              helperText="This is a basic dropdown with items"
            />
            <Typography variant="body2" color="text-muted">
              Selected: {dropdownValue || 'None'}
            </Typography>
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Dropdown Sizes</h4>
          <Stack space="md">
            <Dropdown
              size="sm"
              label="Small Dropdown"
              placeholder="Small size..."
              items={[
                { value: 'small1', label: 'Small Option 1' },
                { value: 'small2', label: 'Small Option 2' },
                { value: 'small3', label: 'Small Option 3' },
              ]}
            />
            <Dropdown
              size="base"
              label="Base Dropdown (Default)"
              placeholder="Base size..."
              items={[
                { value: 'base1', label: 'Base Option 1' },
                { value: 'base2', label: 'Base Option 2' },
                { value: 'base3', label: 'Base Option 3' },
              ]}
            />
            <Dropdown
              size="lg"
              label="Large Dropdown"
              placeholder="Large size..."
              items={[
                { value: 'large1', label: 'Large Option 1' },
                { value: 'large2', label: 'Large Option 2' },
                { value: 'large3', label: 'Large Option 3' },
              ]}
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Dropdown States</h4>
          <Stack space="md">
            <Dropdown
              label="Success Dropdown"
              placeholder="Success state..."
              success
              items={[
                { value: 'success1', label: 'Success Option 1' },
                { value: 'success2', label: 'Success Option 2' },
              ]}
              helperText="This dropdown is in a success state"
            />
            <Dropdown
              label="Error Dropdown"
              placeholder="Error state..."
              error
              items={[
                { value: 'error1', label: 'Error Option 1' },
                { value: 'error2', label: 'Error Option 2' },
              ]}
              helperText="This dropdown has an error"
            />
            <Dropdown
              label="Disabled Dropdown"
              placeholder="Disabled state..."
              disabled
              items={[
                { value: 'disabled1', label: 'Disabled Option 1' },
                { value: 'disabled2', label: 'Disabled Option 2' },
              ]}
              helperText="This dropdown is disabled"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Dropdown with Start Icon</h4>
          <Dropdown
            label="Dropdown with Icon"
            placeholder="Select with icon..."
            items={[
              { value: 'icon1', label: 'Icon Option 1' },
              { value: 'icon2', label: 'Icon Option 2' },
              { value: 'icon3', label: 'Icon Option 3' },
            ]}
            startIcon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>
                <path d="M8 4c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1s1-.45 1-1V5c0-.55-.45-1-1-1zm0 6c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/>
              </svg>
            }
            helperText="This dropdown has a start icon"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Full Width Dropdown</h4>
          <Dropdown
            fullWidth
            label="Full Width Dropdown"
            placeholder="This dropdown takes full width..."
            items={[
              { value: 'full1', label: 'Full Width Option 1' },
              { value: 'full2', label: 'Full Width Option 2' },
              { value: 'full3', label: 'Full Width Option 3' },
            ]}
            helperText="This dropdown spans the full width of its container"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Custom Content Dropdown</h4>
          <Dropdown
            label="Custom Content Dropdown"
            placeholder="Click to see custom content..."
            value={customDropdownValue}
            onChange={setCustomDropdownValue}
            helperText="This dropdown renders custom content instead of items"
          >
            <div style={{ padding: '1rem', color: 'var(--prometheus-text-primary)' }}>
              <Typography variant="h6" color="primary" style={{ marginBottom: '0.5rem' }}>
                Custom Content
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                You can render any custom component here!
              </Typography>
              <Stack space="sm">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setCustomDropdownValue('custom1')}
                >
                  Select Custom Option 1
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCustomDropdownValue('custom2')}
                >
                  Select Custom Option 2
                </Button>
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => setCustomDropdownValue('custom3')}
                >
                  Select Custom Option 3
                </Button>
              </Stack>
            </div>
          </Dropdown>
          <Typography variant="body2" color="text-muted" style={{ marginTop: '0.5rem' }}>
            Selected: {customDropdownValue || 'None'}
          </Typography>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Examples</h4>
          <Stack space="md">
            <Dropdown
              label="Ship Class"
              placeholder="Select your ship class..."
              items={[
                { value: 'fighter', label: 'Fighter - Fast & Agile' },
                { value: 'cruiser', label: 'Cruiser - Balanced' },
                { value: 'battleship', label: 'Battleship - Heavy Armor' },
                { value: 'scout', label: 'Scout - Stealth & Speed' },
                { value: 'carrier', label: 'Carrier - Command Ship' },
              ]}
              helperText="Choose your preferred ship class for combat"
            />
            <Dropdown
              label="Difficulty Level"
              placeholder="Select difficulty..."
              items={[
                { value: 'easy', label: 'Cadet - Easy Mode' },
                { value: 'normal', label: 'Commander - Normal' },
                { value: 'hard', label: 'Admiral - Hard Mode' },
                { value: 'nightmare', label: 'Legend - Nightmare' },
              ]}
              helperText="Higher difficulty = greater rewards"
            />
            <Dropdown
              label="Galaxy Sector"
              placeholder="Choose your starting sector..."
              items={[
                { value: 'alpha', label: 'Alpha Sector - Safe Zone' },
                { value: 'beta', label: 'Beta Sector - Trade Hub' },
                { value: 'gamma', label: 'Gamma Sector - Mining Rich' },
                { value: 'delta', label: 'Delta Sector - Danger Zone' },
              ]}
              helperText="Each sector has unique resources and challenges"
            />
          </Stack>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Checkbox</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Basic Checkboxes</h4>
          <Stack space="md">
            <Checkbox
              label="Basic Checkbox"
              checked={basicCheckbox}
              onChange={(e) => setBasicCheckbox(e.target.checked)}
              helperText="This is a basic checkbox with retro styling"
            />
            <Checkbox
              label="Pre-checked Checkbox"
              checked={checkedCheckbox}
              onChange={(e) => setCheckedCheckbox(e.target.checked)}
              helperText="This checkbox starts in a checked state"
            />
            <Checkbox
              label="Indeterminate Checkbox"
              checked={indeterminateCheckbox}
              indeterminate={true}
              onChange={(e) => setIndeterminateCheckbox(e.target.checked)}
              helperText="This checkbox is in an indeterminate state"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Checkbox Sizes</h4>
          <Stack space="md">
            <Checkbox
              size="sm"
              label="Small Checkbox"
              checked={basicCheckbox}
              onChange={(e) => setBasicCheckbox(e.target.checked)}
              helperText="Small size checkbox with compact styling"
            />
            <Checkbox
              size="base"
              label="Base Checkbox (Default)"
              checked={checkedCheckbox}
              onChange={(e) => setCheckedCheckbox(e.target.checked)}
              helperText="Standard size checkbox"
            />
            <Checkbox
              size="lg"
              label="Large Checkbox"
              checked={indeterminateCheckbox}
              onChange={(e) => setIndeterminateCheckbox(e.target.checked)}
              helperText="Large size checkbox for better visibility"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Checkbox States</h4>
          <Stack space="md">
            <Checkbox
              label="Success Checkbox"
              success
              checked={successCheckbox}
              onChange={(e) => setSuccessCheckbox(e.target.checked)}
              helperText="This checkbox is in a success state"
            />
            <Checkbox
              label="Error Checkbox"
              error
              checked={errorCheckbox}
              onChange={(e) => setErrorCheckbox(e.target.checked)}
              helperText="This checkbox has an error state"
            />
            <Checkbox
              label="Disabled Checkbox (Unchecked)"
              disabled
              checked={false}
              helperText="This checkbox is disabled and unchecked"
            />
            <Checkbox
              label="Disabled Checkbox (Checked)"
              disabled
              checked={true}
              helperText="This checkbox is disabled but checked"
            />
          </Stack>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Full Width Checkbox</h4>
          <Checkbox
            fullWidth
            label="Full Width Checkbox"
            checked={basicCheckbox}
            onChange={(e) => setBasicCheckbox(e.target.checked)}
            helperText="This checkbox spans the full width of its container"
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Examples</h4>
          <Card borderColor="primary" variant="outlined">
            <CardHeader>
              <Typography variant="h6" retro color="primary">GAME SETTINGS</Typography>
            </CardHeader>
            <CardBody>
              <Stack space="md">
                <Checkbox
                  label="Sound Effects Enabled"
                  checked={gameSettings.soundEnabled}
                  onChange={(e) => setGameSettings(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                  helperText="Enable/disable sound effects during gameplay"
                />
                <Checkbox
                  label="Background Music"
                  checked={gameSettings.musicEnabled}
                  onChange={(e) => setGameSettings(prev => ({ ...prev, musicEnabled: e.target.checked }))}
                  helperText="Toggle background music on/off"
                />
                <Checkbox
                  label="Push Notifications"
                  success
                  checked={gameSettings.notifications}
                  onChange={(e) => setGameSettings(prev => ({ ...prev, notifications: e.target.checked }))}
                  helperText="Receive notifications for game events"
                />
                <Checkbox
                  label="Auto-Save Progress"
                  checked={gameSettings.autoSave}
                  onChange={(e) => setGameSettings(prev => ({ ...prev, autoSave: e.target.checked }))}
                  helperText="Automatically save your game progress"
                />
                <Checkbox
                  label="Hardcore Mode"
                  error
                  checked={gameSettings.hardcoreMode}
                  onChange={(e) => setGameSettings(prev => ({ ...prev, hardcoreMode: e.target.checked }))}
                  helperText="⚠️ Warning: No respawning in hardcore mode!"
                />
                <Checkbox
                  label="PvP Combat Enabled"
                  checked={gameSettings.pvpEnabled}
                  onChange={(e) => setGameSettings(prev => ({ ...prev, pvpEnabled: e.target.checked }))}
                  helperText="Allow player vs player combat"
                />
              </Stack>
            </CardBody>
            <CardFooter>
              <Button variant="primary" size="sm">Save Settings</Button>
              <Button variant="secondary" size="sm" outline>Reset to Default</Button>
            </CardFooter>
          </Card>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Mission Selection Example</h4>
          <Card borderColor="info">
            <CardHeader>
              <Typography variant="h6" retro color="info">AVAILABLE MISSIONS</Typography>
            </CardHeader>
            <CardBody>
              <Stack space="md">
                <Checkbox
                  size="lg"
                  label="Secure Alpha Station"
                  checked={true}
                  helperText="Difficulty: Easy | Reward: 1,500 Credits | ETA: 2 hours"
                />
                <Checkbox
                  size="lg"
                  label="Escort Merchant Convoy"
                  checked={false}
                  helperText="Difficulty: Medium | Reward: 3,000 Credits | ETA: 4 hours"
                />
                <Checkbox
                  size="lg"
                  label="Infiltrate Enemy Base"
                  error
                  checked={false}
                  helperText="Difficulty: Hard | Reward: 7,500 Credits | ETA: 8 hours"
                />
                <Checkbox
                  size="lg"
                  label="Legendary Artifact Hunt"
                  disabled
                  checked={false}
                  helperText="Difficulty: Legendary | Reward: ??? | Requires Level 50+"
                />
              </Stack>
            </CardBody>
            <CardFooter>
              <Button variant="success" size="sm">Launch Selected Missions</Button>
            </CardFooter>
          </Card>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Popup</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <h4>Basic Popup Examples</h4>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '20px' }}>
            <Popup
              position="bottom center"
              hoverable
              trigger={
                <Button variant="primary">Hover Me (Hoverable)</Button>
              }
            >
              <Card borderColor="primary" variant="elevated">
                <CardBody>
                  <Typography variant="h6" color="primary">Hoverable Popup</Typography>
                  <Typography variant="body2">
                    This popup stays in place and you can hover over it!
                  </Typography>
                  <Button size="sm" variant="primary" style={{ marginTop: '8px' }}>
                    Action Button
                  </Button>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="top center"
              trigger={
                <Button variant="secondary">Hover Me (Cursor Follow)</Button>
              }
            >
              <Card borderColor="secondary" variant="elevated">
                <CardBody>
                  <Typography variant="h6" color="secondary">Following Popup</Typography>
                  <Typography variant="body2">
                    This popup follows your cursor!
                  </Typography>
                </CardBody>
              </Card>
            </Popup>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Position Examples</h4>
          <div style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Popup
              position="top left"
              hoverable
              trigger={
                <Button variant="info" size="sm">Top Left</Button>
              }
            >
              <Card borderColor="info">
                <CardBody>
                  <Typography variant="body2">Top Left Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="top right"
              hoverable
              trigger={
                <Button variant="success" size="sm">Top Right</Button>
              }
            >
              <Card borderColor="success">
                <CardBody>
                  <Typography variant="body2">Top Right Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="bottom left"
              hoverable
              trigger={
                <Button variant="warning" size="sm">Bottom Left</Button>
              }
            >
              <Card borderColor="warning">
                <CardBody>
                  <Typography variant="body2">Bottom Left Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="bottom right"
              hoverable
              trigger={
                <Button variant="danger" size="sm">Bottom Right</Button>
              }
            >
              <Card borderColor="danger">
                <CardBody>
                  <Typography variant="body2">Bottom Right Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="left center"
              hoverable
              trigger={
                <Button variant="tertiary" size="sm">Left Center</Button>
              }
            >
              <Card borderColor="tertiary">
                <CardBody>
                  <Typography variant="body2">Left Center Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="right center"
              hoverable
              trigger={
                <Button variant="primary" size="sm">Right Center</Button>
              }
            >
              <Card borderColor="primary">
                <CardBody>
                  <Typography variant="body2">Right Center Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="top center"
              hoverable
              trigger={
                <Button variant="secondary" size="sm">Top Center</Button>
              }
            >
              <Card borderColor="secondary">
                <CardBody>
                  <Typography variant="body2">Top Center Position</Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="bottom center"
              hoverable
              trigger={
                <Button variant="info" size="sm">Bottom Center</Button>
              }
            >
              <Card borderColor="info">
                <CardBody>
                  <Typography variant="body2">Bottom Center Position</Typography>
                </CardBody>
              </Card>
            </Popup>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Gaming Style Examples</h4>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Popup
              position="bottom right"
              hoverable
              trigger={
                <Card interactive borderColor="success" style={{ padding: '8px 16px', cursor: 'pointer' }}>
                  <Typography align="center" variant="h6" color="success" retro>CREDITS</Typography>
                  <Typography variant="h4" align="center" retro>12,450</Typography>
                </Card>
              }
            >
              <Card borderColor="success" variant="elevated">
                <CardHeader>
                  <Typography variant="h6" color="success" retro>CREDIT DETAILS</Typography>
                </CardHeader>
                <CardBody>
                  <Stack space="sm">
                    <Typography variant="body2">
                      <strong>Current Balance:</strong> 12,450 Credits
                    </Typography>
                    <Typography variant="body2">
                      <strong>Last Transaction:</strong> +2,500 Credits
                    </Typography>
                    <Typography variant="body2">
                      <strong>Source:</strong> Mission Reward
                    </Typography>
                    <Typography variant="body2">
                      <strong>Daily Earnings:</strong> +3,200 Credits
                    </Typography>
                  </Stack>
                </CardBody>
                <CardFooter>
                  <Button size="sm" variant="success">View Transactions</Button>
                </CardFooter>
              </Card>
            </Popup>

            <Popup
              position="top center"
              trigger={
                <Card interactive borderColor="warning" style={{ padding: '8px 16px', cursor: 'pointer' }}>
                  <Typography align="center" variant="body2" color="warning">HULL</Typography>
                  <Typography variant="h5" align="center" color="warning" retro>75%</Typography>
                </Card>
              }
            >
              <Card borderColor="warning" variant="elevated">
                <CardHeader>
                  <Typography variant="h6" color="warning" retro>SHIP STATUS</Typography>
                </CardHeader>
                <CardBody>
                  <Stack space="sm">
                    <Typography variant="body2">
                      <strong>Hull Integrity:</strong> 75% (Good)
                    </Typography>
                    <Typography variant="body2">
                      <strong>Shield Status:</strong> Online
                    </Typography>
                    <Typography variant="body2">
                      <strong>Engine Status:</strong> Optimal
                    </Typography>
                    <Typography variant="body2">
                      <strong>Weapon Systems:</strong> Armed
                    </Typography>
                  </Stack>
                </CardBody>
                <CardFooter>
                  <Button size="sm" variant="warning">Repair Ship</Button>
                </CardFooter>
              </Card>
            </Popup>

            <Popup
              position="left center"
              hoverable
              trigger={
                <Card interactive borderColor="info" style={{ padding: '8px 16px', cursor: 'pointer' }}>
                  <Typography align="center" variant="body2" color="info">CREW</Typography>
                  <Typography variant="h5" align="center" color="info" retro>1,200</Typography>
                </Card>
              }
            >
              <Card borderColor="info" variant="elevated">
                <CardHeader>
                  <Typography variant="h6" color="info" retro>CREW MANIFEST</Typography>
                </CardHeader>
                <CardBody>
                  <Stack space="sm">
                    <Typography variant="body2">
                      <strong>Total Crew:</strong> 1,200 / 1,500
                    </Typography>
                    <Typography variant="body2">
                      <strong>Officers:</strong> 45
                    </Typography>
                    <Typography variant="body2">
                      <strong>Engineers:</strong> 200
                    </Typography>
                    <Typography variant="body2">
                      <strong>Security:</strong> 150
                    </Typography>
                    <Typography variant="body2">
                      <strong>Medical:</strong> 25
                    </Typography>
                    <Typography variant="body2">
                      <strong>General Crew:</strong> 780
                    </Typography>
                  </Stack>
                </CardBody>
                <CardFooter>
                  <Button size="sm" variant="info">Recruit Crew</Button>
                </CardFooter>
              </Card>
            </Popup>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h4>Smart Positioning Demo</h4>
          <Typography variant="body2" color="text-muted" style={{ marginBottom: '15px' }}>
            Try hovering near the edges of the screen - the popups will automatically adjust to stay visible!
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Popup
              position="right center"
              hoverable
              trigger={
                <Button variant="primary">Left Edge</Button>
              }
            >
              <Card borderColor="primary" variant="elevated">
                <CardBody>
                  <Typography variant="body2">
                    This popup should adjust if it would go off the left edge of the screen.
                  </Typography>
                </CardBody>
              </Card>
            </Popup>

            <Popup
              position="left center"
              hoverable
              trigger={
                <Button variant="secondary">Right Edge</Button>
              }
            >
              <Card borderColor="secondary" variant="elevated">
                <CardBody>
                  <Typography variant="body2">
                    This popup should adjust if it would go off the right edge of the screen.
                  </Typography>
                </CardBody>
              </Card>
            </Popup>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentsDemo;